import { useMemo } from 'react'

import { Code } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { SplitSupplyContractFormFields } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/components/SplitSupplyContractFormFields'
import { useCreateSplitSupplyContract } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/hooks/useCreateSplitSupplyContract'
import { useGetSplitSupplyContractFormOptions } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/hooks/useGetSplitSupplyContractFormOptions'
import {
  EMPTY_FORM_OPTIONS,
  convertFormOptions,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/formOptions'
import {
  emptyFormValues,
  formValuesToRequestFields,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/formValues'
import {
  type SplitSupplyFormValues,
  splitSupplyFormSchema,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/schema'
import { resolveSplitSupplyContractDetailedError } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/serverError'

type Props = {
  isOpen: boolean
  onClose: () => void
}

/**
 * 分割供給契約の新規登録モーダル。 供給地点は顧客管理の候補から選び、
 * 自社の立場を選ぶと自社の側に自社の事業者が入る。 基本パターンも登録時から追加でき、
 * 「保存」で契約と一緒に保存する。
 */
export const CreateSplitSupplyContractModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useCreateSplitSupplyContract()
  const {
    data: formOptionsData,
    isPending: isLoadingFormOptions,
    isError: isFormOptionsError,
    refetch: refetchFormOptions,
  } = useGetSplitSupplyContractFormOptions()
  const options = useMemo(
    () =>
      formOptionsData !== undefined
        ? convertFormOptions(formOptionsData)
        : EMPTY_FORM_OPTIONS,
    [formOptionsData],
  )

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    setValue,
    getValues,
    isDirty,
  } = useCrudModalForm<SplitSupplyFormValues>({
    schema: splitSupplyFormSchema,
    defaultValues: emptyFormValues(),
    onSubmit: async (values) => {
      await mutateAsync({
        supplyPointSpecificNumber: values.supplyPointSpecificNumber,
        ...formValuesToRequestFields(values),
      })
    },
    onClose,
    customErrorKeys: {
      [Code.Aborted]:
        'page:adminOcctoMaster.splitSupplyContract.serverError.aborted',
      [Code.NotFound]:
        'page:adminOcctoMaster.splitSupplyContract.serverError.notFound',
      [Code.InvalidArgument]:
        'page:adminOcctoMaster.splitSupplyContract.serverError.invalidArgument',
    },
    unknownErrorKey:
      'page:adminOcctoMaster.splitSupplyContract.serverError.unknown',
    resolveDetailedError: (err, tt) =>
      resolveSplitSupplyContractDetailedError(err, tt),
  })

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      onClose={handleClose}
      serverError={serverError}
      size="xl"
      title={t('page:adminOcctoMaster.splitSupplyContract.modal.createTitle')}
    >
      <SplitSupplyContractFormFields
        control={control}
        customerName={undefined}
        errors={errors}
        getValues={getValues}
        isFormOptionsError={isFormOptionsError}
        isLoadingFormOptions={isLoadingFormOptions}
        mode="create"
        onRetryFormOptions={() => {
          void refetchFormOptions()
        }}
        options={options}
        savedBusinesses={[]}
        setValue={setValue}
      />
    </CrudModalLayout>
  )
}
